/**
 * Board Service
 * Handles all board-related CRUD operations and sharing
 * 
 * Uses boardRepository for data access and returns ServiceResult<T> for all operations.
 * 
 * @module services/boardService
 */

import { Board, Note, NoteType, ColorTheme, Edge, ViewState, CanvasBackground } from '../types';
import { supabase } from './supabase';
import { getCurrentUserId } from './authService';
import { boardRepository, BoardInsert, BoardUpdate, BoardRowWithPages, PageWithNoteCount } from '../repositories/boardRepository';
import { NoteRow } from '../repositories/noteRepository';
import { ServiceResult, ok, err, isErr, isOk } from '../types/api/result';
export { isErr, isOk };
import { AppError } from '../lib/errors/AppError';
import { logger } from '../lib/logger';
import {
    getPagesByBoardId,
    hasMigratedPages,
    findAllPagesByBoardId,
    findNotesByPageId,
    findEdgesByPageId,
    createPageResult,
    upsertNote,
    upsertEdge
} from './pageService';
import { generateUUID } from '../utils/common';
import { generateSlug } from '../utils/slug';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Transform database row to Board domain object
 * Note: content fields (notes, edges) are loaded from the JSON column 'content' in the row
 */
function toBoard(row: BoardRowWithPages): Board {
    const content = row.content || {};

    // Calculate item count from relational pages or fallback to legacy content
    const relationalCount = row.pages?.reduce((acc: number, p: PageWithNoteCount) => {
        // p.notes is [{ count: N }] from Supabase
        const pageCount = p.notes?.[0]?.count || 0;
        return acc + pageCount;
    }, 0) || 0;

    const legacyCount = (content as { notes?: unknown[] }).notes?.length || 0;

    return {
        id: row.id,
        slug: row.slug,
        title: row.title,
        description: row.description,
        lastEdited: row.last_edited,
        isFavorite: row.is_favorite,
        isDeleted: row.is_deleted,
        themeColor: row.theme_color,
        user_id: row.user_id,
        // Spread content from the JSON column if it exists
        ...(content as Record<string, unknown>),
        // Ensure arrays exist if not in content
        notes: (content as { notes?: Note[] }).notes || [],
        edges: (content as { edges?: Edge[] }).edges || [],
        viewState: (content as { viewState?: ViewState }).viewState || { pan: { x: 0, y: 0 }, zoom: 1 },
        background: (content as { background?: CanvasBackground }).background || 'dots',
        itemCount: relationalCount > 0 ? relationalCount : legacyCount
    };
}

function mapRowToNote(row: NoteRow): Note {
    const metadata = (row.meta || {}) as Record<string, unknown>;
    return {
        id: row.id,
        type: row.type as NoteType,
        x: row.x,
        y: row.y,
        width: row.width || undefined,
        height: row.height || undefined,
        content: row.content || undefined,
        colorTheme: (row.color_theme as ColorTheme) || undefined,
        ...metadata // Spread metadata (styles, custom fields)
    };
}

// =============================================================================
// Service Operations
// =============================================================================

/**
 * Get all boards for the current user
 */
export async function findAllBoards(): Promise<ServiceResult<Board[]>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            // If strictly ensuring auth, we could return error, but legacy returned empty
            // sticking to strict result here.
            return ok([]);
        }

        const rows = await boardRepository.findAll();

        const boards = rows.map(toBoard);

        // The legacy service performed "Retry logic". The repository throws ApiError.
        // We catch ApiError and return err result.

        return ok(boards);
    } catch (error) {
        logger.error('[BoardService] findAllBoards failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Get all boards with full content (including notes from pages)
 * Used for Smart View source picker
 */
export async function findAllBoardsWithContent(): Promise<ServiceResult<Board[]>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) return ok([]);

        const rows = await boardRepository.findAllWithContent();

        const boards = rows.map(row => {
            const board = toBoard(row);

            // Flatten notes from pages if available (Relational Data)
            const rowWithPages = row as BoardRowWithPages;
            const pages = rowWithPages.pages || [];
            if (pages.length > 0) {
                const relationalNotes = pages.flatMap((p) =>
                    ('notes' in p && Array.isArray(p.notes) && p.notes.length > 0 && 'page_id' in p.notes[0])
                        ? (p.notes as NoteRow[]).map((n) => mapRowToNote(n))
                        : []
                );

                // If we found relational notes, use them. 
                // We concatenate with legacy notes only if needed, but usually relational replaces legacy.
                // Assuming fully migrated if pages exist.
                if (relationalNotes.length > 0) {
                    board.notes = relationalNotes;
                }
            }

            return board;
        });

        return ok(boards);
    } catch (error) {
        logger.error('[BoardService] findAllBoardsWithContent failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Get a single board by ID
 */
export async function findBoardById(id: string): Promise<ServiceResult<Board>> {
    try {
        const row = await boardRepository.findById(id);

        if (!row) {
            return err(new AppError('Board not found', {
                code: 'BOARD_NOT_FOUND',
                statusCode: 404,
                context: { id }
            }));
        }

        // The repository findById returns BoardRow which might not have 'content' field typed?
        // In boardRepository.ts: export interface BoardRow { ... }
        // It does NOT have 'content'. 
        // But the Supabase 'select(*)' definitely returns it if it's there.
        // I need to cast row to any to access content for now or update BoardRow.
        const board = toBoard(row);

        return ok(board);
    } catch (error) {
        logger.error('[BoardService] findBoardById failed', error);
        return err(AppError.from(error, { context: { id } }));
    }
}

/**
 * Get a board by its slug
 * Falls back to checking slug_aliases if direct slug not found
 */
export async function findBoardBySlug(slug: string): Promise<ServiceResult<Board>> {
    try {
        // First try direct slug lookup
        let row = await boardRepository.findBySlug(slug);

        // If not found, check slug aliases
        if (!row) {
            const userId = getCurrentUserId();
            if (userId) {
                const { data: aliasData } = await supabase
                    .from('slug_aliases')
                    .select('board_id')
                    .eq('old_slug', slug)
                    .limit(1)
                    .single();

                if (aliasData?.board_id) {
                    logger.debug('[BoardService] Found board via slug alias', { oldSlug: slug, boardId: aliasData.board_id });
                    row = await boardRepository.findById(aliasData.board_id);
                }
            }
        }

        if (!row) {
            return err(new AppError('Board not found', {
                code: 'BOARD_NOT_FOUND',
                statusCode: 404,
                context: { slug }
            }));
        }

        const board = toBoard(row);
        return ok(board);
    } catch (error) {
        logger.error('[BoardService] findBoardBySlug failed', error);
        return err(AppError.from(error, { context: { slug } }));
    }
}

/**
 * Duplicate a board and all its content
 */
export async function duplicateBoard(boardId: string): Promise<ServiceResult<Board>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            return err(new AppError('User not authenticated', {
                code: 'UNAUTHENTICATED',
                statusCode: 401
            }));
        }

        // 1. Fetch original board
        const originalResult = await findBoardById(boardId);
        if (!originalResult.success) return originalResult;
        const originalBoard = originalResult.data;

        // 2. Prepare new board data
        const newBoardId = generateUUID();
        const baseSlug = originalBoard.slug + '-copy';
        let newSlug = baseSlug;
        let counter = 1;

        // Ensure unique slug
        while (await boardRepository.slugExists(newSlug)) {
            newSlug = `${baseSlug}-${counter}`;
            counter++;
        }

        const newBoardInsert: BoardInsert = {
            id: newBoardId,
            user_id: userId,
            title: `Copy of ${originalBoard.title}`,
            description: originalBoard.description,
            slug: newSlug,
            theme_color: originalBoard.themeColor,
            is_favorite: false,
            is_deleted: false,
            content: {
                viewState: originalBoard.viewState,
                background: originalBoard.background
            }
        };

        // 3. Create new board
        await boardRepository.create(newBoardInsert);

        // 4. Duplicate Pages & Content
        const pagesResult = await findAllPagesByBoardId(boardId);
        if (pagesResult.success) {
            for (const page of pagesResult.data) {
                const newPageId = generateUUID();

                // Duplicate Page Metadata
                // createPageResult expects Omit<Page, 'createdAt'>
                const newPageMetadata = {
                    ...page,
                    id: newPageId,
                    // Remove createdAt to let service/db handle it or just ignore since type omits it?
                    // The type definition says Omit createdAt. 
                    // Spreading page includes createdAt, so we should explicitly pick?
                    // Or let TS handle if explicit properties are used.
                    // The 'page' from findAllPagesByBoardId is a full Page.
                };

                // Create the page
                await createPageResult(newPageMetadata, newBoardId);

                // Duplicate Notes
                const notesResult = await findNotesByPageId(page.id);
                if (notesResult.success && notesResult.data.length > 0) {
                    const noteIdMap = new Map<string, string>();

                    // First pass: Create new notes and map IDs
                    for (const note of notesResult.data) {
                        const newNoteId = generateUUID();
                        noteIdMap.set(note.id, newNoteId);

                        await upsertNote({
                            ...note,
                            id: newNoteId
                        }, newPageId);
                    }

                    // Second pass: Duplicate Edges with remapped IDs
                    const edgesResult = await findEdgesByPageId(page.id);
                    if (edgesResult.success && edgesResult.data.length > 0) {
                        for (const edge of edgesResult.data) {
                            const newFrom = noteIdMap.get(edge.from);
                            const newTo = noteIdMap.get(edge.to);

                            if (newFrom && newTo) {
                                await upsertEdge({
                                    ...edge,
                                    id: generateUUID(),
                                    from: newFrom,
                                    to: newTo
                                }, newPageId);
                            }
                        }
                    }
                }
            }
        }

        return await findBoardById(newBoardId);

    } catch (error) {
        logger.error('[BoardService] duplicateBoard failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Create or update a board
 */
export async function upsertBoard(board: Board): Promise<ServiceResult<Board>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            return err(new AppError('User not authenticated', {
                code: 'UNAUTHENTICATED',
                statusCode: 401
            }));
        }

        const {
            id,
            slug,
            title,
            description,
            isFavorite,
            themeColor,
            ...content
        } = board;

        // Verify existence to decide insert vs update?
        // The legacy saveBoard used .upsert() directly.
        // boardRepository split create/update.
        // I should check if it exists or just try update and catch?
        // Upsert is safer for "save" semantics.
        // But boardRepository abstract away specifics.
        // Let's implement upsert logic using repository methods.

        const exists = await boardRepository.findById(id);
        let resultRow;

        // Regenerate slug if title changed
        let finalSlug = slug;
        if (exists && exists.title !== title) {
            // Title changed - regenerate slug
            let baseSlug = generateSlug(title) || 'untitled';
            let candidateSlug = baseSlug;
            let counter = 1;

            // Find unique slug using optimized slugExists check
            while (await boardRepository.slugExists(candidateSlug, id)) {
                candidateSlug = `${baseSlug}-${counter++}`;
                // Safety limit
                if (counter > 1000) {
                    candidateSlug = `${baseSlug}-${Date.now()}`;
                    break;
                }
            }
            finalSlug = candidateSlug;
            logger.debug('[BoardService] Slug regenerated for title change', { oldTitle: exists.title, newTitle: title, oldSlug: slug, newSlug: finalSlug });

            // Store old slug as alias for backwards compatibility
            if (exists.slug && exists.slug !== finalSlug) {
                try {
                    await supabase
                        .from('slug_aliases')
                        .insert({
                            board_id: id,
                            old_slug: exists.slug
                        });
                    logger.debug('[BoardService] Stored slug alias', { boardId: id, oldSlug: exists.slug });
                } catch (aliasError) {
                    // Non-critical: log but don't fail the update
                    logger.warn('[BoardService] Failed to store slug alias', aliasError);
                }
            }
        }

        if (exists) {
            // Update
            const updates: BoardUpdate = {
                title,
                description: description || undefined,
                slug: finalSlug,
                theme_color: themeColor,
                is_favorite: isFavorite,
                last_edited: new Date().toISOString(),
                content: content
            };

            resultRow = await boardRepository.update(id, updates);

        } else {
            const insert: BoardInsert = {
                id,
                user_id: userId,
                title,
                description: description || undefined,
                slug: slug || '',
                theme_color: themeColor,
                is_favorite: isFavorite,
                is_deleted: board.isDeleted,
                content: content
            };
            resultRow = await boardRepository.create(insert);
        }

        return ok(toBoard(resultRow));

    } catch (error) {
        logger.error('[BoardService] upsertBoard failed', error);
        return err(AppError.from(error));
    }
}

/**
 * Get all slugs for collision detection
 */
export async function findAllBoardSlugs(): Promise<ServiceResult<string[]>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) return ok([]);

        // Repository doesn't have "findAllSlugs" but "slugExists".
        // Use direct supabase here or add to generic repo?
        // The legacy used direct select.
        const { data, error } = await supabase
            .from('boards')
            .select('slug')
            .not('slug', 'is', null);

        if (error) throw error;

        const slugs = (data || []).map((b: { slug: string }) => b.slug).filter(Boolean);
        return ok(slugs);
    } catch (error) {
        return err(AppError.from(error));
    }
}

/**
 * Get board with pages
 */
export async function findBoardWithPages(id: string): Promise<ServiceResult<Board>> {
    try {
        // Logic from getBoardWithPages
        const boardResult = await findBoardById(id);
        if (!boardResult.success) return boardResult;

        const board = boardResult.data;
        const hasMigrated = await hasMigratedPages(id);

        if (hasMigrated) {
            const pages = await getPagesByBoardId(id);
            board.pages = pages;
            board.currentPageId = pages[0]?.id;
        }

        return ok(board);
    } catch (error) {
        return err(AppError.from(error));
    }
}


/**
 * Delete a board
 */
export async function deleteBoard(id: string): Promise<ServiceResult<void>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) {
            return err(new AppError('User not authenticated', {
                code: 'UNAUTHENTICATED',
                statusCode: 401
            }));
        }
        await boardRepository.delete(id);
        return ok(undefined);
    } catch (error) {
        logger.error('[BoardService] deleteBoard failed', error);
        return err(AppError.from(error));
    }
}

export const getBoardMembers = async (boardId: string): Promise<BoardMemberProfile[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    const currentUserId = user?.id;

    const { data, error } = await supabase
        .from('board_members')
        .select(`user_id, role, added_at, profiles (email, full_name, avatar_url)`)
        .eq('board_id', boardId);

    interface JoinedBoardMember {
        user_id: string;
        role: string;
        added_at: string;
        profiles: {
            email: string;
            full_name: string;
            avatar_url: string;
        } | null;
    }

    if (error) return [];
    const members = (data as unknown) as JoinedBoardMember[];
    return members.map((row) => ({
        userId: row.user_id,
        role: row.role as 'viewer' | 'commenter' | 'editor',
        addedAt: row.added_at,
        email: row.profiles?.email,
        name: row.profiles?.full_name,
        avatar: row.profiles?.avatar_url,
        isCurrentUser: row.user_id === currentUserId
    }));
};
// ... addBoardMember, removeBoardMember, isBoardOwner similarly ...
export interface BoardMemberProfile {
    userId: string;
    role: 'viewer' | 'commenter' | 'editor';
    addedAt: string;
    email?: string;
    name?: string;
    avatar?: string;
    isCurrentUser?: boolean;
}

export const addBoardMember = async (boardId: string, email: string, role: 'viewer' | 'commenter' | 'editor' = 'editor') => {
    // ...
    // Reuse existing implementation ...
    const { data: users, error: userError } = await supabase.from('profiles').select('id').eq('email', email).single();
    if (userError || !users) return { success: false, message: 'User not found' };

    const { error: insertError } = await supabase.from('board_members').insert({ board_id: boardId, user_id: users.id, role });
    if (insertError) {
        if (insertError.code === '23505') return { success: false, message: 'User is already a member' };
        return { success: false, message: 'Failed to add member' };
    }
    return { success: true, message: 'Member added successfully' };
};

export const removeBoardMember = async (boardId: string, userId: string): Promise<boolean> => {
    const { error } = await supabase.from('board_members').delete().match({ board_id: boardId, user_id: userId });
    return !error;
};

export const isBoardOwner = async (boardId: string): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { data } = await supabase.from('boards').select('user_id').eq('id', boardId).single();
    return data?.user_id === user.id;
};

export interface BoardOwnerProfile {
    userId: string;
    name?: string;
    email?: string;
    avatar?: string;
    isCurrentUser: boolean;
}

/**
 * Get board owner's profile information
 */
export const getBoardOwner = async (boardId: string): Promise<BoardOwnerProfile | null> => {
    const { data: { user } } = await supabase.auth.getUser();

    // Get board owner's user_id
    const { data: board, error: boardError } = await supabase
        .from('boards')
        .select('user_id')
        .eq('id', boardId)
        .single();

    if (boardError || !board) return null;

    // Get owner's profile
    const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, full_name, email, avatar_url')
        .eq('id', board.user_id)
        .single();

    if (profileError || !profile) {
        // Return basic info even if profile fetch fails
        return {
            userId: board.user_id,
            isCurrentUser: user?.id === board.user_id
        };
    }

    return {
        userId: profile.id,
        name: profile.full_name,
        email: profile.email,
        avatar: profile.avatar_url,
        isCurrentUser: user?.id === board.user_id
    };
};

export interface GlobalSearchResult {
    noteId: string;
    noteContent: string;
    noteType: string;
    boardId: string;
    boardTitle: string;
    pageId: string;
}

/**
 * Global Search for Notes across all boards
 * Uses Supabase RPC 'search_global_notes' for performant RLS-aware search
 */
export async function searchGlobalNotes(query: string): Promise<ServiceResult<GlobalSearchResult[]>> {
    try {
        const userId = getCurrentUserId();
        if (!userId) return ok([]);

        const { data, error } = await supabase
            .rpc('search_global_notes', { search_query: query });

        if (error) throw error;

        const results: GlobalSearchResult[] = (data || []).map((row: Record<string, unknown>) => ({
            noteId: row.note_id,
            noteContent: row.note_content || '',
            noteType: row.note_type,
            boardId: row.board_id,
            boardTitle: row.board_title,
            pageId: row.page_id
        }));

        return ok(results);
    } catch (error) {
        logger.error('[BoardService] searchGlobalNotes failed', error);
        return err(AppError.from(error));
    }
}

